import { templateFor } from './dashboardTemplates';

type Lang = 'hr' | 'en';

export interface DashboardHomeProps {
  overview: any;
  lang: Lang;
  program?: string | null;
  onOpen: (panelId: string) => void;
  children?: React.ReactNode;
}

interface WaitingItem {
  id: string;
  panelId: string;
  text: string;
}

const COPY = {
  hr: {
    waiting: 'Čeka tebe',
    loading: 'Učitavanje…',
    nothing: 'Ništa ne čeka tvoju odluku.',
    details: 'Detalji',
    source: 'Izvor podataka nije povezan',
    approvals: (n: number) => `${n} odobrenja čekaju tebe`,
    review: (n: number) => `${n} poslova za pregled`,
    budget: (n: number) => `${n} čeka odobrenje budžeta`,
  },
  en: {
    waiting: 'Waiting for you',
    loading: 'Loading…',
    nothing: 'Nothing is waiting for your decision.',
    details: 'Details',
    source: 'Data source not connected',
    approvals: (n: number) => `${n} approvals waiting for you`,
    review: (n: number) => `${n} jobs to review`,
    budget: (n: number) => `${n} awaiting budget approval`,
  },
};

function toCount(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function DashboardHome({ overview, lang, program, onOpen, children }: DashboardHomeProps) {
  const t = COPY[lang] ?? COPY.hr;
  const template = templateFor(program);

  let waiting: WaitingItem[] | null = null;
  if (overview) {
    const pendingApprovals = toCount(overview.pending_approvals);
    const needsReview = toCount(overview?.jobs?.needs_review);
    const awaitingBudget = toCount(overview?.jobs?.awaiting_budget_approval);
    waiting = [];
    if (pendingApprovals > 0) {
      waiting.push({ id: 'approvals', panelId: 'approvals', text: t.approvals(pendingApprovals) });
    }
    if (needsReview > 0) {
      waiting.push({ id: 'review', panelId: 'jobs', text: t.review(needsReview) });
    }
    if (awaitingBudget > 0) {
      waiting.push({ id: 'budget', panelId: 'approvals', text: t.budget(awaitingBudget) });
    }
  }

  return (
    <div data-testid="dashboard-home" className="space-y-6">
      <section aria-labelledby="dashboard-waiting-heading" className="space-y-3">
        <h2 id="dashboard-waiting-heading" className="text-lg font-semibold">
          {t.waiting}
        </h2>
        {waiting === null ? (
          <p data-testid="dashboard-loading" className="text-muted-foreground">
            {t.loading}
          </p>
        ) : waiting.length > 0 ? (
          <ul data-testid="dashboard-waiting" aria-label={t.waiting} className="space-y-2">
            {waiting.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  data-testid={`dashboard-wait-${item.id}`}
                  onClick={() => onOpen(item.panelId)}
                  className="min-h-11 w-full rounded border border-border bg-primary px-4 py-2 text-left text-primary-foreground"
                >
                  {item.text}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p data-testid="dashboard-nothing-waiting" className="text-muted-foreground">
            {t.nothing}
          </p>
        )}
      </section>

      {template.tiles.length > 0 && (
        <section data-testid="dashboard-tiles" aria-labelledby="dashboard-tiles-heading" className="space-y-3">
          <h2 id="dashboard-tiles-heading" className="text-lg font-semibold">
            {template.title[lang] ?? template.title.hr}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {template.tiles.map((item) => (
              <div
                key={item.id}
                data-testid={`dashboard-tile-${item.id}`}
                className="rounded border border-border p-4"
              >
                <p className="text-sm text-muted-foreground">{item.label[lang] ?? item.label.hr}</p>
                <p data-testid={`dashboard-tile-value-${item.id}`} className="text-2xl">
                  —
                </p>
                <p className="text-xs text-muted-foreground">{t.source}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <details className="rounded border border-border p-4">
        <summary className="cursor-pointer">{t.details}</summary>
        <div className="mt-3">{children}</div>
      </details>
    </div>
  );
}
